import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { BarChart3, RefreshCw, Users, Eye, Clock, ExternalLink } from "lucide-react";

const fmt = new Intl.NumberFormat();
const duration = (s) => {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

function StatBlock({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 bg-slate-50 dark:bg-slate-800/40">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-bold text-slate-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Sparkline({ points, testid }) {
  // Tiny inline SVG sparkline — plots the sessions-per-day trend for the
  // trailing period. Renders nothing until at least 2 real datapoints
  // arrive so we don't paint a flat line for a fresh property.
  if (!Array.isArray(points) || points.length < 2) return null;
  const values = points.map((p) => Number(p.sessions) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const W = 96;
  const H = 28;
  const range = Math.max(1, max - min);
  const step = W / (values.length - 1);
  const coords = values.map((v, i) => {
    const x = i * step;
    const y = H - ((v - min) / range) * (H - 2) - 1;
    return [x, y];
  });
  const linePath = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${W} ${H} L 0 ${H} Z`;
  const last = coords[coords.length - 1];
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible" data-testid={testid}>
      <defs>
        <linearGradient id={`spark-fill-${testid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-fill-${testid})`} />
      <path d={linePath} stroke="#7c3aed" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill="#7c3aed" stroke="#fff" strokeWidth="1" />
    </svg>
  );
}

function PeriodPanel({ days, block }) {
  if (!block) return null;
  const ov = block.overview?.[String(days)] || {};
  const pages = block.top_pages?.[String(days)] || [];
  const countries = block.top_countries?.[String(days)] || [];
  const daily = block.daily_sessions?.[String(days)] || [];
  return (
    <div data-testid={`ga4-period-${days}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Last {days} days</h4>
        {daily.length >= 2 && (
          <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
            <span className="uppercase tracking-widest font-semibold">Sessions trend</span>
            <Sparkline points={daily} testid={`ga4-sparkline-${days}`} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <StatBlock icon={BarChart3} label="Sessions" value={fmt.format(ov.sessions || 0)} />
        <StatBlock icon={Users} label="Users" value={fmt.format(ov.users || 0)} />
        <StatBlock icon={Eye} label="Page views" value={fmt.format(ov.page_views || 0)} />
        <StatBlock icon={Clock} label="Avg session" value={duration(ov.avg_session_duration_seconds || 0)} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Top pages</div>
          {pages.length === 0 && <div className="text-xs text-slate-400 italic">No data yet</div>}
          <ul className="space-y-0.5">
            {pages.slice(0, 5).map((p) => (
              <li key={p.page} className="text-xs text-slate-700 dark:text-slate-300 flex justify-between gap-2">
                <span className="truncate max-w-[70%]" title={p.page}>{p.page || "(not set)"}</span>
                <span className="text-slate-500 dark:text-slate-400 tabular-nums">{fmt.format(p.page_views)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Top countries</div>
          {countries.length === 0 && <div className="text-xs text-slate-400 italic">No data yet</div>}
          <ul className="space-y-0.5">
            {countries.slice(0, 5).map((c) => (
              <li key={c.country} className="text-xs text-slate-700 dark:text-slate-300 flex justify-between gap-2">
                <span className="truncate max-w-[70%]" title={c.country}>{c.country || "(not set)"}</span>
                <span className="text-slate-500 dark:text-slate-400 tabular-nums">{fmt.format(c.sessions)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function GA4OverviewCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const { data } = await API.get(`/admin/ga4/overview${refresh ? "?refresh=true" : ""}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load GA4 data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const propertyId = data?.property_id || "";
  const notConfigured = data && data.configured === false;
  const apiError = data && data.error;

  return (
    <div
      className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5"
      data-testid="ga4-overview-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-orange-600 dark:text-orange-300" />
          </div>
          <div>
            <h3
              className="font-semibold text-slate-900 dark:text-white"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Google Analytics 4 Overview
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Live site traffic — refreshes every 10 minutes
              {propertyId ? ` · Property ${propertyId}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {propertyId && (
            <a
              href={`https://analytics.google.com/analytics/web/#/p${propertyId}/reports/intelligenthome`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-500 hover:text-purple-600 flex items-center gap-1"
              title="Open in Google Analytics"
              data-testid="ga4-open-in-ga"
            >
              Open in GA <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1 disabled:opacity-50"
            data-testid="ga4-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400 dark:text-slate-500 py-4">Loading GA4 report…</div>}

      {!loading && error && (
        <div className="text-sm text-rose-600 dark:text-rose-400 py-2" data-testid="ga4-error">
          {error}
        </div>
      )}

      {!loading && notConfigured && (
        <div className="text-sm text-slate-500 dark:text-slate-400 py-2">
          GA4 is not configured on the backend. Set <code>GA4_PROPERTY_ID</code> and{" "}
          <code>GA4_SERVICE_ACCOUNT_JSON</code> in the backend .env, then restart.
        </div>
      )}

      {!loading && apiError && (
        <div className="text-sm text-rose-600 dark:text-rose-400 py-2" data-testid="ga4-api-error">
          GA4 API error: {apiError}
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Common cause: the service account isn&apos;t added to the GA4 property
            (Admin → Property access management → Add users →{" "}
            <code>glpflo-ga-reader@glpflo-llc.iam.gserviceaccount.com</code> → Viewer).
          </div>
        </div>
      )}

      {!loading && data && !notConfigured && !apiError && !error && (
        <div className="space-y-5">
          <PeriodPanel days={7} block={data} />
          <PeriodPanel days={28} block={data} />
          {data.cached && (
            <div className="text-[10px] text-slate-400 dark:text-slate-500">
              Cached · generated {new Date((data.generated_at || 0) * 1000).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
